import axios from 'axios';
import { API_URL } from '../config';
import { getTokens } from '../utils/tokens';
import { createBrowserHistory } from 'history';
import { AppDispatch } from 'store/store';
import { BaseQueryFn, FetchArgs, FetchBaseQueryError, fetchBaseQuery } from '@reduxjs/toolkit/dist/query';
import queryString from 'query-string';

const history = createBrowserHistory();
const pathArray = history.location.pathname.split('/');
const supportedLangs = ['en', 'ru', 'es', 'pt'];

export const instance = axios.create({
	baseURL: API_URL,
});

// Функция для получения текущего языка
const getCurrentLanguage = () => {
	const langFromUrl = window.location.pathname.split('/')[1];
	return supportedLangs.includes(langFromUrl) ? langFromUrl : 'ru';
};

export const setupAxiosInterceptors = async (dispatch: AppDispatch) => {
	const search = queryString.parse(history.location.search);
	const isPublic = search.public === 'true';

	instance.interceptors.request.use(
		(config) => {
			const { token } = getTokens();
			if (token) {
				config.headers.Authorization = `Bearer ${token}`;
			}

			// Добавляем заголовок языка
			config.headers['Accept-Language'] = getCurrentLanguage();

			return config;
		},
		(error) => {
			return Promise.reject(error);
		},
	);

	instance.interceptors.response.use(
		(response) => response,
		async (error) => {
			if (!pathArray.includes('call')) {
				if (error.response) {
					// Для 401 ошибки
					if (error.response.status === 401) {
						if (!isPublic) {
							await dispatch({ type: 'user/setAuth', payload: false });
							await dispatch({ type: 'user/removeAuthToken' });
							history.push(`/${getCurrentLanguage()}/auth`);
						}
						// Возвращаем стандартизированную ошибку
						return Promise.reject({
							message: 'Unauthorized',
							status: 401,
							response: error.response,
						});
					}

					// Для других ошибок
					return Promise.reject({
						message: error.response.data?.detail || 'Server error',
						status: error.response.status,
						response: error.response,
					});
				} else {
					// Для случаев, когда нет response (например, сетевые ошибки)
					return Promise.reject({
						message: 'Network error',
						status: 0,
						isNetworkError: true,
					});
				}
			}
			return Promise.reject(error);
		},
	);
};

const baseQuery = fetchBaseQuery({
	baseUrl: API_URL,
	prepareHeaders: (headers) => {
		const { token } = getTokens();
		if (token) {
			headers.set('authorization', `Bearer ${token}`);
		}
		// Добавляем заголовок языка
		headers.set('Accept-Language', getCurrentLanguage());
		return headers;
	},
});

export const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
	args,
	api,
	extraOptions,
) => {
	const search = queryString.parse(history.location.search);
	const isPublic = search.public === 'true';

	const result = await baseQuery(args, api, extraOptions);

	if (result.error && result.error.status === 401) {
		if (!isPublic) {
			const dispatch = api.dispatch as AppDispatch;
			await dispatch({ type: 'user/setAuth', payload: false });
			localStorage.clear();
			sessionStorage.clear();
			history.push(`/${getCurrentLanguage()}/auth`);
			return result;
		} else {
			return { data: {} };
		}
	}

	return result;
};
